(ns ductile.ui.agent
  "AI Agent chat interface for Ductile.

   Uses Mistral Conversations API for stateful chat. The server maintains
   conversation history — only new inputs are sent each turn.

   Tool-call loop runs in the frontend:
   1. Send user message to ductile.agent/chat → get conversation-id + response
   2. If response contains :tool-calls, execute each tool (server or client-side)
   3. Send tool results as FunctionResultEntries to the same conversation
   4. Repeat until the model returns a final text response"
  (:require ["markdown-it" :as MarkdownIt]
            [cljs.pprint :as pprint]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.ui.agent-stream :as agent.stream]
            [ductile.ui.agent.artifacts.damages :as artifacts.damages]
            [ductile.ui.agent.artifacts.table :as artifacts.table]
            [ductile.ui.agent.artifacts.vega-lite :as artifacts.vega-lite]
            [ductile.ui.agent.artifacts.vehicle :as artifacts.vehicle]
            [ductile.ui.agent.orchestration :as agent.orch]
            [ductile.ui.agent.policy :as agent.policy]
            [ductile.ui.agent.tools :as tools]
            [ductile.ui.components :as ui]
            [ductile.ui.http :as http]
            [ductile.ui.locale :as locale :refer [tr]]
            [reagent.core :as r]))

;; DEBUG (remove after streaming stabilizes)
(defonce ^:private !debug-stream-events (r/atom []))
(defonce ^:private !debug-stream-state (r/atom {:status :idle
                                                :request-id nil
                                                :last-event nil
                                                :last-tool-calls nil
                                                :last-tool-call-deltas nil}))
(defonce ^:private !debug-stream-text (r/atom ""))
(defonce ^:private !debug-stream-handle (atom nil))
(defonce ^:private !debug-turns (r/atom []))

(defonce ^:private md (MarkdownIt.))

(defn- streaming-enabled?
  "Return true unless the current URL contains ?streaming=off.

  DEBUG-friendly: allows switching between streaming + legacy (non-streaming)
  behavior without rebuilding."
  []
  (try
    (let [params (js/URLSearchParams. (.-search js/window.location))
          v (.get params "streaming")]
      (not= "off" v))
    (catch :default _
      true)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; State

(def ^:private initial-state
  {:messages []
   :loading? false
   :conversation-id nil
   :user-requested-visualization? false
   :turn-resolver-used? false
   :turn-resolver-recommended-key nil
   :artifacts []
   :artifacts-panel-open? false
   :selected-artifact-id nil
   :artifact-parent-by-id {}
   :artifact-breadcrumb-root-id nil
   :tool-datasets {}
   :latest-dataset-id nil
   :tool-call-cache {}
   :turn-eval-used? false
   :turn-eval-response nil})

(defonce !state (r/atom initial-state))

;; Cursors for targeted reactivity — behave like standalone atoms
(def !messages (r/cursor !state [:messages]))
(def !loading (r/cursor !state [:loading?]))
(def !conversation-id (r/cursor !state [:conversation-id]))
(def !user-requested-visualization? (r/cursor !state [:user-requested-visualization?]))
(def !turn-resolver-used? (r/cursor !state [:turn-resolver-used?]))
(def !turn-resolver-recommended-key (r/cursor !state [:turn-resolver-recommended-key]))
(def !artifacts (r/cursor !state [:artifacts]))
(def !artifacts-panel-open? (r/cursor !state [:artifacts-panel-open?]))
(def !selected-artifact-id (r/cursor !state [:selected-artifact-id]))
(def !artifact-parent-by-id (r/cursor !state [:artifact-parent-by-id]))
(def !artifact-breadcrumb-root-id (r/cursor !state [:artifact-breadcrumb-root-id]))
(def !tool-datasets (r/cursor !state [:tool-datasets]))
(def !latest-dataset-id (r/cursor !state [:latest-dataset-id]))
(def !tool-call-cache (r/cursor !state [:tool-call-cache]))
(def !turn-eval-used? (r/cursor !state [:turn-eval-used?]))
(def !turn-eval-response (r/cursor !state [:turn-eval-response]))

(def ^:private max-tool-rounds 10)
(def ^:private max-tool-datasets 30)
(def ^:private max-function-result-chars 12000)
(declare register-dataset!)
(declare dataset-data)

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Tool Utilities

(defn- user-requested-visualization?
  [text]
  (tools/user-requested-visualization? text))

(defn- last-user-message-text
  []
  (tools/last-user-message-text @!messages))

(defn- dataset-context-keys
  [dataset-id]
  (agent.policy/dataset-context-keys @!tool-datasets @!latest-dataset-id dataset-id))

(defn- tool-label [tool-name]
  (tools/tool-label tool-name))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Tool Registry Wrappers

(defn- tool-cacheable? [tool-name]
  (tools/tool-cacheable? tool-name))

(defn- compact-tool-result [tool-name result]
  (case tool-name
    "vehicle-search" (tools/vehicle-search-compact result register-dataset!)
    "query-one" (tools/query-one-compact result register-dataset!)
    "count-vehicles" (tools/count-vehicles-compact result)
    "eval-clojure" (tools/eval-clojure-compact result)
    result))

(defn- tool-result->artifact [tool-name result]
  (case tool-name
    "vehicle-search" (tools/vehicle-search-artifact result)
    "query-one" (tools/query-one-artifact result)
    nil))

(defn- execute-tool-handler [tool-name params]
  (case tool-name
    "display-artifact" (js/Promise.resolve {:ok true})
    "eval-clojure" (js/Promise.resolve (tools/eval-clojure params @!tool-datasets @!latest-dataset-id))
    (http/$lambda 'ductile.agent/run-tool
                  {:tool-name tool-name :tool-params params})))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Tool execution

(defn- display-artifact-ui-payload
  [tool-params result]
  (tools/display-artifact-ui-payload tool-params result))

(defn- make-tool-message
  "Build a tool result message map."
  [{:keys [tool-call-id tool-name duration-ms arguments result result-for-llm]}]
  {:tool_call_id tool-call-id
   :role "tool"
   :content (js/JSON.stringify (clj->js (or result-for-llm result)))
   :_ui/result result
   :_ui/result-for-llm result-for-llm
   :_ui/tool-meta {:tool-call-id tool-call-id
                   :tool-name tool-name
                   :duration-ms duration-ms
                   :arguments arguments}})

(defn- compact-function-result-content
  [content]
  (if (<= (count content) max-function-result-chars)
    content
    (js/JSON.stringify
     (clj->js {:ok false
               :error "result-too-large"
               :error-message (tr "Function result payload exceeded size limit.")
               :content-size (count content)}))))

(defn- trim-tool-datasets!
  []
  (when (> (count @!tool-datasets) max-tool-datasets)
    (let [sorted-ids (->> @!tool-datasets
                          (sort-by (fn [[_ v]] (:created-at v)))
                          (map first)
                          vec)
          to-drop (take (- (count @!tool-datasets) max-tool-datasets) sorted-ids)]
      (swap! !tool-datasets #(apply dissoc % to-drop)))))

(defn- register-dataset!
  [kind data]
  (let [dataset-id (str (random-uuid))]
    (swap! !tool-datasets assoc dataset-id {:kind kind
                                            :data data
                                            :created-at (.now js/Date)})
    (reset! !latest-dataset-id dataset-id)
    (trim-tool-datasets!)
    dataset-id))

(defn- fahrzeug-has-damages?
  [fahrzeug]
  (or (seq (:vehicle/damages-outside fahrzeug))
      (seq (:vehicle/damages-inside fahrzeug))))

(defn- damages-artifact-from-fahrzeug
  [fahrzeug]
  (when (and (map? fahrzeug) (fahrzeug-has-damages? fahrzeug))
    {:id (str (random-uuid))
     :type "damages"
     :payload {:fahrzeug fahrzeug}
     :title (str (tr "Damages") " — " (or (:vehicle/vin fahrzeug) (tr "Vehicle")))
     :status :ready
     :created-at (.now js/Date)}))

(defn- intent-facet-group
  [intent]
  (let [v (or (:intent/facet-group intent) (:facet-group intent))]
    (cond
      (keyword? v) (name v)
      (string? v) v
      :else nil)))

(defn- damages-artifact-from-eval-result
  [result]
  (let [intent (:resolved-intent result)
        dataset-id (:dataset-id result)]
    (when (and (= "damages" (intent-facet-group intent))
               (string? dataset-id)
               (not (str/blank? dataset-id)))
      {:id (str (random-uuid))
       :type "damages"
       :payload {:dataset-id dataset-id}
       :title (tr "Damages")
       :status :ready
       :created-at (.now js/Date)})))


(defn- execute-tool-call
  "Execute a single tool call. Client-side tools (eval-clojure, display-artifact)
   run locally; others go to the run-tool lambda.
   Returns a promise of the tool result message."
  [{:keys [id function]}]
  (let [raw-tool-name (:name function)
        started-at (.now js/Date)
        tool-args (:arguments function)
        base-tool-params (js->clj (js/JSON.parse tool-args) :keywordize-keys true)
        tool-name (if (and (= "vehicle-search" raw-tool-name)
                           (= 1 (count (cond
                                         (string? (get-in base-tool-params [:query :vehicle/vin])) [(get-in base-tool-params [:query :vehicle/vin])]
                                         (string? (get-in base-tool-params [:query "vehicle/vin"])) [(get-in base-tool-params [:query "vehicle/vin"])]
                                         (set? (get-in base-tool-params [:query :vehicle/vin])) (get-in base-tool-params [:query :vehicle/vin])
                                         (set? (get-in base-tool-params [:query "vehicle/vin"])) (get-in base-tool-params [:query "vehicle/vin"])
                                         :else []))))
                    "query-one"
                    raw-tool-name)
        tool-params (if (= "resolve-attribute-intent" tool-name)
                      (update base-tool-params :context-keys
                              (fn [ks]
                                (or (seq ks)
                                    (dataset-context-keys (:dataset-id base-tool-params)))))
                      base-tool-params)
        cache-key (str tool-name "::" (pr-str tool-params))
        make-msg (fn [result result-for-llm]
                   (let [duration-ms (- (.now js/Date) started-at)]
                     (cond-> (make-tool-message {:tool-call-id id
                                                 :tool-name tool-name
                                                 :duration-ms duration-ms
                                                 :arguments tool-args
                                                 :result result
                                                 :result-for-llm result-for-llm})
                       (and (= "display-artifact" tool-name) (:ok result))
                       (assoc :_ui/artifact (display-artifact-ui-payload tool-params result))
                       (not= "display-artifact" tool-name)
                       (assoc :_ui/artifact (tool-result->artifact tool-name result)))))
        cache-result! (fn [result result-for-llm]
                        (when (tool-cacheable? tool-name)
                          (swap! !tool-call-cache assoc cache-key {:result result
                                                                   :result-for-llm result-for-llm})))
        store-eval! (fn [result result-for-llm]
                      (reset! !turn-eval-used? true)
                      (reset! !turn-eval-response {:result result
                                                   :result-for-llm result-for-llm})
                      (cache-result! result result-for-llm))
        cached (get @!tool-call-cache cache-key)]
    (cond
      (and cached (tool-cacheable? tool-name))
      (js/Promise.resolve
       (assoc-in (make-msg (:result cached) (:result-for-llm cached))
                 [:_ui/tool-meta :cached?] true))

      (and (= "eval-clojure" tool-name) @!turn-eval-used?)
      (let [first-eval @!turn-eval-response
            result {:ok false
                    :error "eval-limit-exceeded"
                    :error-message (tr "Only one eval-clojure call is allowed per user turn. Reuse the first eval result.")
                    :first-eval-summary (select-keys (:result-for-llm first-eval)
                                                     [:result-kind :item-count :non-empty? :row-count :entry-count :value-preview])}]
        (js/Promise.resolve
         (assoc-in (make-msg result result)
                   [:_ui/tool-meta :deduped?] true)))

      (= "eval-clojure" tool-name)
      (if (and (not @!turn-resolver-used?)
               (agent.policy/eval-references-attr-key? (:code tool-params)))
        (let [user-text (last-user-message-text)
              dataset-context (dataset-context-keys (:dataset-id tool-params))
              run-attribute-preflight
              (fn []
                (let [resolver-params {:text user-text
                                       :context-keys dataset-context}]
                  (-> (http/$lambda 'ductile.agent/run-tool
                                    {:tool-name "resolve-attribute-intent"
                                     :tool-params resolver-params})
                      (.then (fn [resolver-result]
                               (let [recommended-key (:recommended-key resolver-result)
                                     ambiguous? (:ambiguous? resolver-result)
                                     _ (reset! !turn-resolver-used? true)
                                     _ (reset! !turn-resolver-recommended-key recommended-key)
                                     referenced-keys (agent.policy/eval-referenced-attr-keys (:code tool-params))
                                     context-keys (set dataset-context)
                                     referenced-root-keys (agent.policy/referenced-root-keys context-keys referenced-keys)
                                     resolver-agrees? (or (nil? recommended-key) (contains? referenced-keys recommended-key))
                                     can-rewrite? (and recommended-key
                                                       (not ambiguous?)
                                                       (= 1 (count referenced-root-keys))
                                                       (not resolver-agrees?)
                                                       (agent.policy/rewrite-safe?
                                                        {:tool-datasets @!tool-datasets
                                                         :latest-dataset-id @!latest-dataset-id
                                                         :dataset-id (:dataset-id tool-params)
                                                         :old-key-str (first (seq referenced-root-keys))
                                                         :new-key-str recommended-key}))
                                     original-code (:code tool-params)
                                     rewritten-code (when can-rewrite?
                                                      (let [old-key (first (seq referenced-root-keys))]
                                                        (str/replace original-code old-key recommended-key)))
                                     eval-params (if rewritten-code
                                                   (assoc tool-params :code rewritten-code)
                                                   tool-params)
                                     result (cond-> (tools/eval-clojure eval-params @!tool-datasets @!latest-dataset-id)
                                              recommended-key
                                              (assoc :recommended-key recommended-key
                                                     :original-code original-code
                                                     :effective-code (or rewritten-code original-code))
                                              recommended-key
                                              (assoc :note (cond
                                                             resolver-agrees?
                                                             "Resolver preflight validated eval expression."
                                                             rewritten-code
                                                             "Resolver preflight corrected eval root key using recommended key."
                                                             :else
                                                             "Resolver preflight disagreed with eval expression; executed original code as source of truth.")))
                                     result-for-llm (compact-tool-result tool-name result)]
                                 (store-eval! result result-for-llm)
                                 (make-msg result result-for-llm))))
                      (.catch (fn [err]
                                (let [result {:ok false
                                              :error "resolver-execution-error"
                                              :error-message (.-message err)}]
                                  (make-msg result (compact-tool-result tool-name result))))))))]
          (-> (http/$lambda 'ductile.agent/run-tool
                            {:tool-name "resolve-query-intent"
                             :tool-params {:text user-text}})
              (.then (fn [intent-result]
                       (if (agent.orch/service-intent? intent-result)
                         (let [eval-code (agent.orch/service-intent-eval-code intent-result)
                               _ (reset! !turn-resolver-used? true)
                               _ (reset! !turn-resolver-recommended-key (:recommended-key intent-result))
                               eval-params (assoc tool-params :code eval-code)
                               result (assoc (tools/eval-clojure eval-params @!tool-datasets @!latest-dataset-id)
                                             :recommended-key (:recommended-key intent-result)
                                             :original-code (:code tool-params)
                                             :effective-code eval-code
                                             :note "Service intent preflight generated deterministic eval code."
                                             :resolved-intent intent-result)
                               result-for-llm (compact-tool-result tool-name result)]
                           (store-eval! result result-for-llm)
                           (make-msg result result-for-llm))
                         (if (agent.orch/composite-intent? intent-result)
                           (let [eval-code (agent.orch/composite-intent-eval-code intent-result)
                                 _ (reset! !turn-resolver-used? true)
                                 _ (reset! !turn-resolver-recommended-key (:recommended-key intent-result))
                                 eval-params (assoc tool-params :code eval-code)
                                 result (assoc (tools/eval-clojure eval-params @!tool-datasets @!latest-dataset-id)
                                               :recommended-key (:recommended-key intent-result)
                                               :original-code (:code tool-params)
                                               :effective-code eval-code
                                               :note "Composite intent preflight generated deterministic eval code."
                                               :resolved-intent intent-result)
                                 result-for-llm (compact-tool-result tool-name result)]
                             (store-eval! result result-for-llm)
                             (make-msg result result-for-llm))
                           (run-attribute-preflight)))))
              (.catch (fn [_]
                        (run-attribute-preflight)))))
        (let [recommended-key @!turn-resolver-recommended-key
              referenced-keys (agent.policy/eval-referenced-attr-keys (:code tool-params))]
          (if (and recommended-key
                   (seq referenced-keys)
                   (not (contains? referenced-keys recommended-key)))
            (let [result (assoc (tools/eval-clojure tool-params @!tool-datasets @!latest-dataset-id)
                                :note "Resolver disagreed with eval expression; executed original code as source of truth."
                                :recommended-key recommended-key
                                :referenced-keys (vec (sort referenced-keys))
                                :original-code (:code tool-params)
                                :effective-code (:code tool-params))
                  result-for-llm (compact-tool-result tool-name result)]
              (store-eval! result result-for-llm)
              (js/Promise.resolve (make-msg result result-for-llm)))
            (let [result (tools/eval-clojure tool-params @!tool-datasets @!latest-dataset-id)
                  result-for-llm (compact-tool-result tool-name result)]
              (store-eval! result result-for-llm)
              (js/Promise.resolve (make-msg result result-for-llm))))))

      (and (= "display-artifact" tool-name)
           (not @!user-requested-visualization?))
      (let [result {:ok false
                    :error "display-artifact-not-requested"
                    :error-message (tr "Display-artifact is disabled unless the user explicitly asks for a chart/table/visualization.")}]
        (js/Promise.resolve (make-msg result result)))

      :else
      (-> (execute-tool-handler tool-name tool-params)
          (.then (fn [result]
                   (let [result-for-llm (compact-tool-result tool-name result)]
                     (when (= "resolve-attribute-intent" tool-name)
                       (reset! !turn-resolver-used? true)
                       (reset! !turn-resolver-recommended-key (:recommended-key result-for-llm)))
                     (cache-result! result result-for-llm)
                     (make-msg result result-for-llm))))
          (.catch (fn [err]
                    (let [result {:ok false
                                  :error "execution-error"
                                  :error-message (.-message err)}]
                      (make-msg result (compact-tool-result tool-name result)))))))))

(defn- execute-all-tool-calls
  "Execute all tool calls in order. Sequential execution avoids races between
   data-fetch tools and eval-clojure over their datasets."
  [tool-calls]
  (reduce (fn [acc-p tool-call]
            (.then acc-p
                   (fn [acc]
                     (.then (execute-tool-call tool-call)
                            (fn [tool-msg]
                              (conj acc tool-msg))))))
          (js/Promise.resolve [])
          tool-calls))

(defn- aggregate-tool-artifacts
  "If the model calls query-one multiple times in one round (e.g. VIN lists),
   collapse individual fahrzeug artifacts into a single table artifact."
  [tool-messages]
  (let [resolve-domain (fn [result]
                         (let [d (or (:intent/domain result) (:domain result))]
                           (cond
                             (keyword? d) (name d)
                             (string? d) d
                             :else nil)))
        damages-requested? (or
                            (boolean
                             (some (fn [m]
                                     (and (= "resolve-query-intent" (get-in m [:_ui/tool-meta :tool-name]))
                                          (= "damages" (resolve-domain (:_ui/result m)))))
                                   tool-messages))
                            (boolean
                             (re-find #"\b(damage|damages|schaden|schäden|scratch|dent)\b"
                                      (str/lower-case (str (last-user-message-text))))))
        explicit-display-artifact? (boolean (some #(= "display-artifact" (get-in % [:_ui/tool-meta :tool-name])) tool-messages))
        query-one-fahrzeuge (->> tool-messages
                                 (filter #(= "query-one" (get-in % [:_ui/tool-meta :tool-name])))
                                 (keep (fn [m]
                                         (let [result (:_ui/result m)]
                                           (when (and (:found result) (map? (:fahrzeug result)))
                                             (:fahrzeug result)))))
                                 vec)
        aggregate? (and (not explicit-display-artifact?)
                        (> (count query-one-fahrzeuge) 1))
        aggregated-table-artifact (when aggregate?
                                    {:id (str (random-uuid))
                                     :type "table"
                                     :payload {:rows query-one-fahrzeuge}
                                     :title (tr "Vehicle search results")
                                     :caption (tr "Aggregated from multiple single-vehicle lookups")
                                     :status :ready
                                     :created-at (.now js/Date)})
        normalized-tool-messages (if aggregate?
                                   (mapv (fn [m]
                                           (if (= "query-one" (get-in m [:_ui/tool-meta :tool-name]))
                                             (dissoc m :_ui/artifact)
                                             m))
                                         tool-messages)
                                   tool-messages)
        auto-artifacts (->> normalized-tool-messages
                            (filter #(not= "display-artifact" (get-in % [:_ui/tool-meta :tool-name])))
                            (keep :_ui/artifact)
                            vec)
        explicit-artifacts (->> normalized-tool-messages
                                (filter #(= "display-artifact" (get-in % [:_ui/tool-meta :tool-name])))
                                (keep :_ui/artifact)
                                vec)
        damage-artifacts (if damages-requested?
                           (->> normalized-tool-messages
                                (mapcat (fn [m]
                                          (let [tool-name (get-in m [:_ui/tool-meta :tool-name])
                                                result (:_ui/result m)]
                                            (cond
                                              (and (= "query-one" tool-name) (:found result) (map? (:fahrzeug result)))
                                              (keep identity [(damages-artifact-from-fahrzeug (:fahrzeug result))])

                                              (and (= "vehicle-search" tool-name) (sequential? (:fahrzeuge result)))
                                              (->> (:fahrzeuge result)
                                                   (take 10)
                                                   (keep damages-artifact-from-fahrzeug))

                                              (and (= "eval-clojure" tool-name) (map? result))
                                              (keep identity [(damages-artifact-from-eval-result result)])

                                              :else
                                              []))))
                                vec)
                           [])
        auto-types (set (map :type auto-artifacts))
        explicit-artifacts (->> explicit-artifacts
                                (filter (fn [artifact]
                                          (let [type (:type artifact)]
                                            (or (= "vega-lite" type)
                                                (not (contains? #{"table" "fahrzeug"} type))
                                                (not (contains? auto-types type))))))
                                vec)
        artifacts (cond-> (vec (concat auto-artifacts damage-artifacts explicit-artifacts))
                    aggregated-table-artifact (conj aggregated-table-artifact))]
    {:tool-messages normalized-tool-messages
     :artifacts artifacts}))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Chat loop

(defn- chat-round
  "Send inputs to the chat endpoint (single Mistral conversation round).
   If conversation-id is set, continues existing conversation."
  [inputs]
  (let [params (cond-> {:inputs inputs}
                 @!conversation-id (assoc :conversation-id @!conversation-id))]
    (http/$lambda 'ductile.agent/chat params)))

(defn- collect-artifacts!
  [artifacts]
  (let [artifacts (vec artifacts)]
    (when (seq artifacts)
      (swap! !artifacts into artifacts))
    artifacts))

(defn- tool-messages->function-results
  "Convert executed tool messages to Mistral FunctionResultEntry format."
  [tool-messages]
  (letfn [(safe-result-for-llm [tool-msg]
            (let [tool-meta (get tool-msg :_ui/tool-meta)
                  result-for-llm (get tool-msg :_ui/result-for-llm)
                  result (get tool-msg :_ui/result)
                  tool-name (:tool-name tool-meta)]
              (case tool-name
                "vehicle-search"
                (if (map? result-for-llm)
                  (select-keys result-for-llm [:total-count :returned-count :truncated? :dataset-id :dataset-kind :llm/note])
                  {:ok false
                   :error "missing-compact-result"
                   :error-message "Missing compact vehicle-search result."})

                "query-one"
                (if (map? result-for-llm)
                  (select-keys result-for-llm [:found :dataset-id :dataset-kind :vehicle/vin :error :error-message])
                  {:ok false
                   :error "missing-compact-result"
                   :error-message "Missing compact query-one result."})

                "count-vehicles"
                (if (map? result-for-llm)
                  (select-keys result-for-llm [:count :llm/note])
                  {:ok false
                   :error "missing-compact-result"
                   :error-message "Missing compact count-vehicles result."})

                "resolve-attribute-intent"
                (select-keys (or result-for-llm result)
                             [:query :recommended-key :ambiguous? :matches :error :error-message])

                "resolve-query-intent"
                (select-keys (or result-for-llm result)
                             [:query :domain :operation :service-state
                              :intent/domain :intent/operation :intent/service-state
                              :intent/facet-group :intent/policy :intent/exclusive? :intent/keys
                              :recommended-key :target-service-code :confidence :error :error-message])

                "eval-clojure"
                (select-keys (or result-for-llm result)
                             [:ok :source-dataset-id :dataset-id :result-kind :item-count :non-empty? :row-count :entry-count :columns :top-entries :value-preview :error :error-message :recommended-key :referenced-keys :original-code :effective-code :note :resolved-intent :llm/note])

                "display-artifact"
                (select-keys (or result-for-llm result) [:ok :artifact-id :artifact-type :error :error-message])

                (select-keys (or result-for-llm result) [:ok :error :error-message]))))]
    (mapv (fn [{:keys [tool_call_id] :as tool-msg}]
            (let [result-payload (safe-result-for-llm tool-msg)
                  result-str (-> result-payload clj->js js/JSON.stringify compact-function-result-content)]
              {:type "function.result"
               :tool_call_id tool_call_id
               :result result-str}))
          tool-messages)))

(defn- append-error-message!
  "Append an error message and stop loading."
  [text]
  (swap! !messages conj {:role "assistant" :content text})
  (reset! !loading false))

(defn- append-assistant-message!
  "Append a final assistant text response and stop loading."
  [content]
  (swap! !messages conj {:role "assistant" :content (or content "(empty response)")})
  (reset! !loading false))

(defn- ensure-streaming-assistant-message!
  "Ensure there is an in-progress assistant message at the end of the message list.

  We stream tokens into this message so the user sees the accumulated answer
  while it is being generated."
  []
  (swap! !messages
         (fn [msgs]
           (if (and (= "assistant" (:role (last msgs)))
                    (true? (:streaming? (last msgs))))
             msgs
             (conj (vec msgs)
                   {:role "assistant"
                    :content ""
                    :streaming? true}))))
  (dec (count @!messages)))

(defn- append-stream-token!
  "Append a streamed token to the current in-progress assistant message."
  [token]
  (when (string? token)
    (swap! !messages
           (fn [msgs]
             (let [idx (dec (count msgs))]
               (if (and (>= idx 0)
                        (= "assistant" (get-in msgs [idx :role])))
                 (update-in msgs [idx :content] (fnil str "") token)
                 msgs))))))

(defn- build-artifact-previews
  "Build compact preview maps for artifact display in tool-call messages."
  [artifacts]
  (mapv (fn [artifact]
          (cond-> (select-keys artifact [:id :title :type :created-at])
            (= "table" (:type artifact))
            (assoc :result-count
                   (or (some-> artifact :payload :rows count)
                       (some-> artifact :payload :dataset-id
                               (get @!tool-datasets) :data count)
                       0))))
        artifacts))

(defn- build-tool-metas
  "Build a map of tool-call-id → tool metadata from executed tool messages."
  [tool-messages]
  (reduce (fn [acc m]
            (let [meta (:_ui/tool-meta m)
                  tool-call-id (:tool-call-id meta)
                  llm-result (:_ui/result-for-llm m)]
              (if tool-call-id
                (assoc acc tool-call-id (assoc meta :llm-result llm-result))
                acc)))
          {}
          tool-messages))

(defn- mark-tools-done!
  "Update the last assistant message to show tools completed with previews and metas."
  [artifact-previews tool-metas]
  (swap! !messages
         (fn [msgs]
           (let [idx (dec (count msgs))]
             (cond-> (assoc-in msgs [idx :tool-status] :done)
               (seq artifact-previews) (assoc-in [idx :artifact-previews] artifact-previews)
               (seq tool-metas) (assoc-in [idx :tool-metas] tool-metas))))))

(defn- show-artifacts!
  "Add artifacts to the panel and select the last one."
  [artifacts]
  (let [new-artifacts (collect-artifacts! artifacts)]
    (when (seq new-artifacts)
      (reset! !artifacts-panel-open? true)
      (reset! !selected-artifact-id (:id (last new-artifacts))))))

(defn- tool-round-limit-reached?
  "Check if we've hit the maximum number of tool rounds."
  []
  (>= (count (filter #(= "tool" (:role %)) @!messages))
      max-tool-rounds))

(defn- process-tool-round!
  "Execute tool calls, update UI state, and return a promise of function-result
   entries for the next chat round. Resolves to nil when the round limit is reached."
  [tool-calls]
  (swap! !messages conj {:role "assistant" :tool-calls tool-calls :tool-status :calling})
  (-> (execute-all-tool-calls tool-calls)
      (.then
       (fn [raw-tool-messages]
         (let [{:keys [tool-messages artifacts]} (aggregate-tool-artifacts raw-tool-messages)]
           (mark-tools-done! (build-artifact-previews artifacts) (build-tool-metas tool-messages))
           (show-artifacts! artifacts)
           (swap! !messages into tool-messages)
           (if (tool-round-limit-reached?)
             (do (append-error-message! (tr "I've reached the maximum number of tool calls. Please try a simpler question."))
                 nil)
             (tool-messages->function-results tool-messages)))))))

(defn- run-chat-loop!
  "Run the tool-call loop. Calls the LLM, processes tool calls, and recurses
   until the model returns a final text response or an error occurs."
  [inputs]
  (-> (chat-round inputs)
      (.then
       (fn [response]
         (when-let [cid (:conversation-id response)]
           (reset! !conversation-id cid))
         (cond
           (:error response)
           (append-error-message! (str (tr "Error") ":" (:error-message response)))

           (seq (:tool-calls response))
           (-> (process-tool-round! (:tool-calls response))
               (.then (fn [function-results]
                        (when function-results
                          (run-chat-loop! function-results))))
               (.catch (fn [err]
                         (append-error-message! (str (tr "Tool execution error") ": " (.-message err))))))

           :else
           (append-assistant-message! (:content response)))))
      (.catch
       (fn [err]
         (append-error-message! (str (tr "Error") ":" (.-message err)))))))


(defn- stop-stream!
  "Stop an active streaming request, if any."
  []
  (when-let [h @!debug-stream-handle]
    (try
      ((:abort! h))
      (catch :default _))
    (reset! !debug-stream-handle nil))
  (swap! !debug-stream-state assoc :status :idle))

(defn- run-streaming-chat!
  "Start streaming assistant output from the dedicated HTTP endpoint.

  NOTE: This streams assistant text AND tool-call deltas. The client implements
  the tool-call loop.

  DEBUG: pushes events and accumulated text into !debug-stream-* atoms."
  [inputs]
  (stop-stream!)
  (ensure-streaming-assistant-message!)
  (reset! !loading true)
  (swap! !debug-stream-events (fnil conj []) {:ts (.now js/Date)
                                              :event "client/start"
                                              :data {:inputs (if (string? inputs) (subs inputs 0 (min 100 (count inputs))) "<complex>")}})
  (reset! !debug-stream-text "")
  (swap! !debug-stream-state assoc :status :streaming :last-event "client/start")
  ;; DEBUG
  (swap! !debug-turns conj {:ts (.now js/Date)
                            :phase :stream-round-start
                            :conversation-id @!conversation-id
                            :inputs-kind (if (string? inputs) :text :function-results)})
  (let [resolve! (atom nil)
        reject! (atom nil)
        done-p (js/Promise.
                (fn [res rej]
                  (reset! resolve! res)
                  (reset! reject! rej)))
        ;; local per-round buffer
        !round (atom {:tool-call-deltas []
                      :finish-reason nil
                      :conversation-id nil})

        !resolved? (atom false)
        h (agent.stream/stream!
           {:url "/api/agent/stream"
            :body (cond-> {:inputs inputs}
                    @!conversation-id (assoc :conversation-id @!conversation-id))
            :on-open (fn []
                       (swap! !debug-stream-state assoc :status :open :last-event "open"))
            :on-event (fn [{:keys [event data raw] :as ev}]
                        (swap! !debug-stream-events conj (assoc ev :ts (.now js/Date)))
                        (swap! !debug-stream-state assoc :last-event event)
                        (case event
                          "meta" (when-let [rid (:request-id data)]
                                   (swap! !debug-stream-state assoc :request-id rid))

                          "token" (let [t (:text data)]
                                    (swap! !debug-stream-text str (or t ""))
                                    (append-stream-token! (or t "")))

                          "tool_calls" (do
                                         ;; Backend now forwards tool-call deltas. Buffering/reassembly happens client-side.
                                         (swap! !round assoc :tool-call-deltas (:tool-call-deltas data))
                                         (swap! !debug-stream-state assoc :last-tool-call-deltas (:tool-call-deltas data)))

                          "done" (do
                                   (when-let [cid (:conversation-id data)]
                                     (reset! !conversation-id cid)
                                     (swap! !round assoc :conversation-id cid))
                                   (swap! !round assoc :finish-reason (:finish-reason data))
                                   ;; DEBUG
                                   (swap! !debug-turns conj {:ts (.now js/Date)
                                                             :phase :stream-round-done
                                                             :conversation-id (:conversation-id @!round)
                                                             :finish-reason (:finish-reason data)
                                                             :tool-call-delta-count (count (:tool-call-deltas @!round))})
                                   (swap! !messages update-in [(dec (count @!messages))] assoc :streaming? false)
                                   (reset! !loading false)
                                   (swap! !debug-stream-state assoc :status :done)
                                   (when (and (not @!resolved?)
                                              (some? @resolve!))
                                     (reset! !resolved? true)
                                     (@resolve! @!round)))

                          "client/closed" nil

                          "error" (do
                                    ;; Upstream error (often indicates broken conversation state).
                                    ;; Reset conversation-id so the next user message starts fresh.
                                    (reset! !conversation-id nil)
                                    (append-error-message! (str (tr "Error") ": " (or (:message data) raw)))
                                    (swap! !debug-stream-state assoc :status :error)
                                    (reset! !loading false)
                                    (when-let [rej @reject!]
                                      (rej (js/Error. (or (:message data) raw "stream error")))))
                          nil))
            :on-error (fn [err]
                        (swap! !debug-stream-events conj {:ts (.now js/Date)
                                                          :event "client/error"
                                                          :data {:message (.-message err)}})
                        ;; Reset conversation-id so the next user message starts fresh.
                        (reset! !conversation-id nil)
                        (append-error-message! (str (tr "Error") ": " (.-message err)))
                        (swap! !debug-stream-state assoc :status :error)
                        (reset! !loading false)
                        (when-let [rej @reject!]
                          (rej err)))
            :on-done (fn []
                       (swap! !debug-stream-events conj {:ts (.now js/Date)
                                                         :event "client/closed"
                                                         :data {}})
                       (swap! !debug-stream-state assoc :status :closed)
                       ;; If stream closes without an explicit `done`, finalize UI state
                       ;; so the thinking indicator doesn't stick around.
                       (when (and (not @!resolved?)
                                  (nil? (:finish-reason @!round))
                                  (some? @resolve!))
                         (swap! !messages update-in [(dec (count @!messages))] assoc :streaming? false)
                         (reset! !loading false)
                         (reset! !resolved? true)
                         (@resolve! @!round)))})]
    (reset! !debug-stream-handle h)
    (assoc h :done done-p)))

(defn- tool-call-deltas->tool-calls
  "Reassemble tool-call deltas emitted by the streaming endpoint.

  deltas is a vector of maps like {:id ... :function {:name ... :arguments \"...\"}}.
  Returns vector of tool-call maps compatible with execute-tool-call/process-tool-round!."
  [deltas]
  (->> (reduce (fn [acc call]
                 (let [id (:id call)
                       prev (get acc id)
                       prev-args (get-in prev [:function :arguments] "")
                       args (get-in call [:function :arguments])
                       next-args (if (string? args) (str prev-args args) prev-args)
                       next (cond-> (or prev call)
                              (some? (get-in call [:function :name])) (assoc-in [:function :name] (get-in call [:function :name]))
                              (string? args) (assoc-in [:function :arguments] next-args))]
                   (assoc acc id next)))
               {}
               (or deltas []))
       vals
       vec))

;; DEBUG (remove after streaming stabilizes)
(declare debug-append-turn!)

(defn- debug-append-turn!
  [m]
  (swap! !debug-turns conj (assoc m :ts (.now js/Date))))

(defn- send-message-non-streaming!
  [text]
  (run-chat-loop! text))

(defn- send-message-streaming!
  [text]
  (letfn [(run-loop! [inputs]
            (-> (run-streaming-chat! inputs)
                :done
                (.then (fn [{:keys [finish-reason tool-call-deltas conversation-id]}]
                         (when (= "tool_calls" finish-reason)
                           (let [tool-calls (tool-call-deltas->tool-calls tool-call-deltas)
                                 expected-ids (set (map :id tool-calls))]
                             (debug-append-turn! {:phase :tool-calls-received
                                                  :conversation-id conversation-id
                                                  :expected-tool-call-ids expected-ids
                                                  :tool-call-count (count tool-calls)})
                             (-> (process-tool-round! tool-calls)
                                 (.then (fn [function-results]
                                          (let [produced-ids (->> @!messages
                                                                  (filter #(= "tool" (:role %)))
                                                                  (keep :tool_call_id)
                                                                  set)
                                                missing (seq (set/difference expected-ids produced-ids))]
                                            (debug-append-turn! {:phase :tool-round-finished
                                                                 :conversation-id @!conversation-id
                                                                 :produced-tool-call-ids produced-ids
                                                                 :missing-tool-call-ids (set missing)})
                                            (when (seq missing)
                                              (append-error-message!
                                               (str "Tool execution did not produce results for tool_call_id(s): " missing
                                                    ". Conversation may be out of sync.")))
                                            (when function-results
                                              (debug-append-turn! {:phase :append-function-results
                                                                   :conversation-id @!conversation-id
                                                                   :function-result-count (count function-results)
                                                                   :function-result-ids (set (map :tool_call_id function-results))})
                                              (run-loop! function-results))))))))))))]
    (run-loop! text)))

(defn send-message!
  "Send a user message and start streaming."
  [text]
  (when-not (str/blank? text)
    (swap! !state #(-> %
                       (assoc :tool-call-cache {}
                              :user-requested-visualization? (user-requested-visualization? text)
                              :turn-resolver-used? false
                              :turn-resolver-recommended-key nil
                              :turn-eval-used? false
                              :turn-eval-response nil
                              :loading? true)
                       (update :messages conj {:role "user" :content text})))
    (if (streaming-enabled?)
      (send-message-streaming! text)
      (send-message-non-streaming! text))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; UI Components

(defn- artifact-error-card [{:keys [error-message correction-hint error-type invalid-spec]}]
  [:div.border.border-red-200.bg-red-50.text-red-700.rounded-lg.p-3.text-sm
   [:div.font-medium.mb-1 (tr "Artifact rendering failed")]
   [:div.mb-2 (or error-message (tr "Invalid artifact payload."))]
   (when correction-hint
     [:div.mt-2.p-2.bg-yellow-50.border.border-yellow-200.rounded.text-yellow-800
      [:div.font-medium.text-xs.mb-1 (tr "Correction hint for LLM:")]
      [:div.text-xs.correction-hint correction-hint]])
   (when invalid-spec
     [:details.mt-2
      [:summary.text-xs.cursor-pointer "Show invalid spec"]
      [:pre.mt-1.text-xs.overflow-auto.max-h-40
       (with-out-str (cljs.pprint/pprint invalid-spec))]])])

(defn- format-artifact-timestamp [created-at]
  (when created-at
    (.toLocaleString (js/Date. created-at))))

(defn- artifact-title [{:keys [title]}]
  (or (not-empty title) (tr "Untitled artifact")))

(defn- artifact-breadcrumb-label
  [artifact]
  (if (= "fahrzeug" (:type artifact))
    (let [fahrzeug (get-in artifact [:payload :fahrzeug])
          vin (or (some-> fahrzeug :vehicle/vin str)
                  (not-empty (:title artifact))
                  (tr "Vehicle"))
          model (some-> fahrzeug :vehicle/model str)]
      (if (and (string? model) (not (str/blank? model)))
        (str vin " • " model)
        vin))
    (artifact-title artifact)))

(defn- breadcrumb-path
  [selected-id parent-by-id root-id]
  (loop [curr selected-id
         seen #{}
         acc []]
    (if (or (nil? curr) (contains? seen curr))
      (vec (reverse acc))
      (let [acc' (conj acc curr)
            parent-id (get parent-by-id curr)
            stop? (or (nil? parent-id)
                      (= curr root-id))]
        (if stop?
          (vec (reverse acc'))
          (recur parent-id (conj seen curr) acc'))))))

(defn- table-result-count [artifact]
  (when (= "table" (:type artifact))
    (or (:result-count artifact)
        (some-> artifact :payload :rows count)
        (some-> artifact :payload :dataset-id dataset-data count)
        0)))

(defn- artifact-meta-line [artifact]
  (let [ts (format-artifact-timestamp (:created-at artifact))
        result-count (table-result-count artifact)]
    (when (or ts (some? result-count))
      [:div.text-xs.text-slate-500
       (str (or ts "")
            (when (some? result-count)
              (str (when ts " • ") result-count " results")))])))

(defn- artifact-type->icon [type]
  (case type
    "vega-lite" :bar-chart
    "table" :table
    "fahrzeug" :car
    "damages" :damage
    :file-text))

(defn- dataset-data
  [dataset-id]
  (get-in @!tool-datasets [dataset-id :data]))

(defn- open-vehicle-from-table-row!
  [parent-artifact-id vin]
  (when (and (string? vin) (not (str/blank? vin)))
    (-> (http/$lambda 'ductile.agent/run-tool
                      {:tool-name "query-one"
                       :tool-params {:query {:vehicle/vin vin}}})
        (.then (fn [result]
                 (when (and (:found result) (map? (:fahrzeug result)))
                   (let [artifact {:id (str (random-uuid))
                                   :type "fahrzeug"
                                   :payload {:fahrzeug (:fahrzeug result)}
                                   :title (or (some-> result :fahrzeug :vehicle/vin str)
                                              (tr "Vehicle"))
                                   :status :ready
                                   :created-at (.now js/Date)}]
                     (swap! !artifacts conj artifact)
                     (when parent-artifact-id
                       (swap! !artifact-parent-by-id assoc (:id artifact) parent-artifact-id))
                     (reset! !artifacts-panel-open? true)
                     (reset! !selected-artifact-id (:id artifact))))))
        (.catch (fn [_] nil)))))

(defn- artifact-preview-row
  [{:keys [selected? on-click]} artifact]
  [:button.w-full.text-left.rounded-lg.border.border-slate-200.bg-slate-50.hover:bg-slate-100.px-3.py-2
   {:class (when selected? "bg-slate-100 border-slate-300")
    :on-click on-click}
   [:div.flex.items-center.gap-3
    [ui/icon {:size 24 :class "text-slate-500 flex-shrink-0"} (artifact-type->icon (:type artifact))]
    [:div.flex-1.min-w-0
     [:div.text-sm.text-slate-800.truncate (artifact-title artifact)]
     [artifact-meta-line artifact]]
    [ui/icon {:size 16 :class "text-slate-400 flex-shrink-0"} :chevron-right]]])

(defn- artifact-card [{:keys [id type spec title caption status] :as artifact}]
  [:div.rounded-xl.border.border-slate-200.bg-white.shadow-sm.p-3
   {:key id}
   [:div.mb-2
    [:div.font-semibold.text-sm.text-slate-900 (artifact-title artifact)]
    [artifact-meta-line artifact]]
   (cond
     (= :error status)
     [artifact-error-card artifact]

     (= "vega-lite" type)
     [artifacts.vega-lite/view artifact
      {:dataset-data dataset-data
       :error-view artifact-error-card}]

     (= "table" type)
     [artifacts.table/view artifact
      {:dataset-data dataset-data
       :on-row-click (fn [vin _row]
                       (open-vehicle-from-table-row! @!selected-artifact-id vin))}]

     (= "fahrzeug" type)
     [artifacts.vehicle/view artifact
      {:dataset-data dataset-data
       :error-view artifact-error-card}]

     (= "damages" type)
     [artifacts.damages/view artifact
      {:dataset-data dataset-data
       :error-view artifact-error-card}]

     :else
     [artifact-error-card {:error-message (str (tr "Unsupported artifact type: ") type)}])
   (when (seq caption)
     [:div.text-xs.text-slate-500.mt-2 caption])])

(defn- artifact-previews-display [previews]
  [:div.flex.flex-col.gap-2.mt-2
   (for [artifact previews]
     ^{:key (:id artifact)}
     [artifact-preview-row
      {:on-click #(do (reset! !artifacts-panel-open? true)
                      (reset! !selected-artifact-id (:id artifact)))}
      artifact])])

(defn- artifact-panel []
  (r/with-let [!root-menu-open? (r/atom false)
               !crumbs-el (atom nil)
               !last-path-sig (r/atom nil)]
    (let [artifacts @!artifacts
          selected-id @!selected-artifact-id
          parent-by-id @!artifact-parent-by-id
          root-id @!artifact-breadcrumb-root-id
          selected-artifact (or (some #(when (= selected-id (:id %)) %) artifacts)
                                (last artifacts))
          artifact-by-id (into {} (map (juxt :id identity) artifacts))
          selected-id (some-> selected-artifact :id)
          path (if selected-id
                 (breadcrumb-path selected-id parent-by-id root-id)
                 [])
          path-sig (str/join "|" path)
          root-choice-id (or (first path) selected-id)
          root-artifact (get artifact-by-id root-choice-id)
          prev-id (when (> (count path) 1) (nth path (- (count path) 2)))]
      (when (not= path-sig @!last-path-sig)
        (reset! !last-path-sig path-sig)
        (js/requestAnimationFrame
         (fn []
           (when-let [el @!crumbs-el]
             (when (> (.-scrollWidth el) (.-clientWidth el))
               (set! (.-scrollLeft el) (.-scrollWidth el)))))))
      [:div.flex-1.min-w-0.h-full.bg-white.rounded-xl.border.border-slate-200.shadow-sm.flex.flex-col
       (when @!root-menu-open?
         [:div.fixed.inset-0.z-20
          {:on-click #(reset! !root-menu-open? false)}])
       [:div.relative.p-4.border-b.border-slate-200.flex.items-center.justify-between
        [:div.min-w-0.flex-1.overflow-x-auto.whitespace-nowrap
         {:ref #(reset! !crumbs-el %)}
         [:div.inline-flex.items-center.min-w-max
          [:div.inline-flex.items-center.shrink-0
           [:button.inline-flex.h-6.items-center.text-xs.leading-none.text-slate-700.hover:text-slate-900
            {:on-click #(do (when root-choice-id
                              (reset! !selected-artifact-id root-choice-id))
                            (reset! !root-menu-open? false))}
            [:span (artifact-breadcrumb-label root-artifact)]]
           [:button.inline-flex.h-6.w-5.items-center.justify-center.text-slate-400.hover:text-slate-700
            {:on-click #(swap! !root-menu-open? not)}
            [ui/icon {:size 12} :chevron-down]]]
          (for [artifact-id (rest path)
                :let [artifact (get artifact-by-id artifact-id)]]
            ^{:key (str "crumb-" artifact-id)}
            [:<>
             [:span.text-slate-400.mx-1.shrink-0 "/"]
             (if (= artifact-id selected-id)
               [:span.inline-flex.h-6.items-center.text-xs.leading-none.text-slate-700.shrink-0
                (artifact-breadcrumb-label artifact)]
               [:button.inline-flex.h-6.items-center.text-xs.leading-none.text-slate-600.hover:text-slate-900.shrink-0
                {:on-click #(reset! !selected-artifact-id artifact-id)}
                (artifact-breadcrumb-label artifact)])])]]
        [:div.flex.items-center.gap-2.ml-6.shrink-0
         (when prev-id
           [:<>
            [:button.inline-flex.h-6.items-center.text-xs.leading-none.text-slate-500.hover:text-slate-700
             {:on-click #(reset! !selected-artifact-id prev-id)}
             (tr "Back")]
            [:div.h-4.w-px.bg-slate-200]])
         [:button.inline-flex.h-6.w-6.items-center.justify-center.text-slate-400.hover:text-slate-600
          {:on-click #(reset! !artifacts-panel-open? false)}
          [ui/icon {:size 16} :x]]]
        (when @!root-menu-open?
          [:div.absolute.left-4.top-full.mt-1.z-30.max-h-64.overflow-y-auto.rounded-md.border.border-slate-200.bg-white.shadow-lg
           {:class "w-[320px]"
            :on-click #(.stopPropagation %)}
           (for [artifact (reverse artifacts)]
             ^{:key (str "root-opt-" (:id artifact))}
             [:button.w-full.text-left.px-3.py-2.text-xs.hover:bg-slate-50.border-b.last:border-b-0.border-slate-100
              {:on-click #(do (reset! !artifact-breadcrumb-root-id (:id artifact))
                              (reset! !selected-artifact-id (:id artifact))
                              (reset! !root-menu-open? false))}
              [:div.text-slate-800.truncate (artifact-breadcrumb-label artifact)]])])]
       [:div.flex-auto.overflow-y-auto.p-4.space-y-4
        (if (seq artifacts)
          [:<>
           [artifact-card selected-artifact]]
          [:div.text-sm.text-slate-400 (tr "No artifacts yet.")])]])))

(defn- tool-calls-display
  "Render tool calls in the chat."
  [{:keys [tool-calls tool-status tool-metas]}]
  (letfn [(parse-json-safe [s]
            (try
              (js->clj (js/JSON.parse (or s "{}")) :keywordize-keys true)
              (catch :default _ nil)))
          (tool-args-view [tool-name args llm-result]
            (if (= "eval-clojure" tool-name)
              (let [parsed (parse-json-safe args)
                    requested-code (:code parsed)
                    effective-code (or (:effective-code llm-result) requested-code)
                    original-code (or (:original-code llm-result) requested-code)
                    dataset-id (:dataset-id parsed)
                    resolved? (and (string? effective-code)
                                   (string? original-code)
                                   (not= effective-code original-code))]
                [:div.mt-1.p-2.bg-slate-50.rounded.text-xs.text-slate-700.space-y-1
                 (when dataset-id
                   [:div {:class "text-[11px] text-slate-500"} (str "dataset-id: " dataset-id)])
                 [:div {:class "text-[11px] text-slate-500"} (tr "Effective code")]
                 [:pre.p-2.bg-white.rounded.text-xs.text-slate-700.overflow-x-auto
                  (or effective-code args "{}")]
                 (when resolved?
                   [:div
                    [:div {:class "text-[11px] text-slate-500"} (tr "Resolved from")]
                    [:pre {:class "p-2 bg-slate-100 rounded text-[11px] text-slate-500 overflow-x-auto"}
                     original-code]])])
              [:pre.mt-1.p-2.bg-slate-50.rounded.text-xs.text-slate-600.overflow-x-auto
               (or args "{}")]))]
    [:div.space-y-1
     (for [[idx tc] (map-indexed vector tool-calls)]
       (let [tool-id (or (:id tc) (str "tool-" idx))
             args (or (get-in tc [:function :arguments])
                      (get-in tool-metas [tool-id :arguments]))
             tool-name (or (get-in tc [:function :name]) (str "tool-" (inc idx)))
             duration-ms (get-in tool-metas [tool-id :duration-ms])
             llm-result (get-in tool-metas [tool-id :llm-result])
             cached? (true? (get-in tool-metas [tool-id :cached?]))
             deduped? (true? (get-in tool-metas [tool-id :deduped?]))]
         ^{:key tool-id}
         [:details.group.rounded
          [:summary.cursor-pointer.list-none.text-sm.text-slate-500.hover:text-slate-700.flex.gap-1
           [:span.font-medium (if (= :calling tool-status) (tr "Calling") (tr "Done"))]
           [:span (tool-label tool-name)]
           (when cached?
             [:span.text-slate-400 "[cached]"])
           (when deduped?
             [:span.text-slate-400 "[deduped]"])
           (when duration-ms
             [:span.text-slate-400 (.toFixed (/ duration-ms 1000) 2) "s"])]
          [tool-args-view tool-name args llm-result]
          (when (some? llm-result)
            [:pre.mt-1.p-2.bg-emerald-50.rounded.text-xs.text-emerald-700.overflow-x-auto
             (js/JSON.stringify (clj->js llm-result) nil 2)])]))]))

(defn- markdown-content
  "Render a markdown string as HTML."
  [text]
  (let [rendered-html (.render md text)
        parser (js/DOMParser.)
        doc (.parseFromString parser rendered-html "text/html")
        blocked-selectors ["img" "svg" "canvas" "picture" "video" "audio"
                           "iframe" "object" "embed"]
        blocked-nodes (->> blocked-selectors
                           (mapcat (fn [selector]
                                     (array-seq (.querySelectorAll doc selector)))))]
    (doseq [node blocked-nodes]
      (let [replacement (.createElement doc "span")]
        (set! (.-className replacement) "text-slate-500 italic")
        (set! (.-textContent replacement) (tr "[artifact moved to Artifacts panel]"))
        (.replaceWith node replacement)))
    [:div.prose.prose-sm.max-w-none.text-sm
     {:dangerouslySetInnerHTML {:__html (.-innerHTML (.-body doc))}}]))

(defn- message-bubble [{:keys [role content tool-calls tool-status artifact-previews tool-metas] :as msg}]
  (case role
    "user"
    [:div.flex.justify-end
     [:div {:class "max-w-[75%]"}
      [:div.inline-block.bg-indigo-50.text-slate-800.rounded-lg.px-4.py-2.text-sm
       content]]]

    "assistant"
    [:div.flex
     [:div.flex-1.max-w-prose
      (when (seq tool-calls)
        [tool-calls-display {:tool-calls tool-calls
                             :tool-status tool-status
                             :tool-metas tool-metas}])
      (when (seq artifact-previews)
        [artifact-previews-display artifact-previews])
      (when content
        [markdown-content content])]]

    ;; tool messages are internal, don't render
    nil))

(defn view []
  (r/with-let [!input (r/atom "")
               !messages-el (atom nil)]
    (do
      (r/after-render
       (fn []
         (js/requestAnimationFrame
          (fn []
            (when-let [el @!messages-el]
              (set! (.-scrollTop el) (.-scrollHeight el)))))))
      [:div.flex.h-full
       {:class (when @!artifacts-panel-open? "gap-4")
        :style {:height "calc(100vh - 50px - 54px)"}}
       [:div.flex.flex-col.h-full.flex-1.min-w-0.bg-white.rounded-xl.border.border-slate-200.shadow-sm
        ;; Header
        [:div.p-4.border-b.border-slate-200.flex.items-center.justify-between
         [:div.flex.items-center.gap-2.text-sm
          [:div.font-bold "Optimus Alpha 1"]
          [:div.text-slate-500 " — " (tr "Ask about vehicles, movements, or status.")]]
         [:div.flex.items-center.gap-2
          (when (or (seq @!messages) (seq @!artifacts))
            [:button.text-xs.text-slate-400.hover:text-slate-600.inline-flex.items-center.gap-1
             {:on-click #(reset! !state initial-state)}
             [ui/icon {:size 16} :eraser]
             (tr "Clear")])]]

        ;; Messages
        [:div.flex-auto.overflow-y-auto.p-4.space-y-4
         {:ref #(reset! !messages-el %)}
         (if (empty? @!messages)
           [:div.text-center.text-slate-400.mt-20]
           (for [[i msg] (map-indexed vector @!messages)
                 :when (not= "tool" (:role msg))]
             ^{:key i}
             [message-bubble msg]))
         (when @!loading
           [:div.text-sm
            [:span.thinking-shine (tr "Thinking...")]])]

        ;; Input
        [:div.p-3.pb-2.border-t.border-slate-200.bg-slate-50.rounded-b-xl
         [:div.flex-1.relative
          [:textarea.flex-1.resize-none.text-sm.w-full.focus:outline-none.focus:ring-2.focus:ring-indigo-600.border.border-slate-300.focus:border-white.rounded-lg.p-3.bg-white
           {:rows 2
            :placeholder (tr "Type your question...")
            :value @!input
            :disabled @!loading
            :spellcheck "false"
            :on-change #(reset! !input (.. % -target -value))
            :on-key-down (fn [e]
                           (when (and (= "Enter" (.-key e)) (not (.-shiftKey e)))
                             (.preventDefault e)
                             (when-not @!loading
                               (let [text (str/trim @!input)]
                                 (when-not (str/blank? text)
                                   (reset! !input "")
                                   (send-message! text))))))}]
          [:div.flex.justify-end
           [ui/button
            {:variant :primary
             :class ["flex" "absolute" "bottom-4" "right-2"]
             :disabled (or (str/blank? @!input) @!loading)
             :on-click (fn []
                         (let [text (str/trim @!input)]
                           (when-not (str/blank? text)
                             (reset! !input "")
                             (send-message! text))))}
            (if @!loading
              [ui/spinner {:size 16}]
              [ui/icon {:size 16} :send])]]]]]
       (when @!artifacts-panel-open?
         [artifact-panel])])))
